import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Gauge, Star, BadgeCheck } from "lucide-react";
import { formatPrice, formatMileage, timeAgo } from "@/lib/constants";
import { absUrl } from "@/lib/api";

/**
 * Unified listing card — native + external listings look identical.
 *   - External listings click straight through to the source URL (nofollow noopener sponsored).
 *   - No fake "External"/"Aggregated" badges. Photo disclaimer is the only tell.
 *   - Sharp corners, no pills — brutalist Mopar aesthetic.
 */
export default function ListingCard({ listing, testid }) {
    const hero = listing.photos?.[0] || listing.stock_image;
    const isExternal = Boolean(
        listing.source && listing.source !== "native" && listing.external_url
    );
    const hasDisclaimer = Boolean(listing.photo_disclaimer);

    const linkProps = isExternal
        ? {
              as: "a",
              href: listing.external_url,
              target: "_blank",
              rel: "nofollow noopener sponsored",
          }
        : { as: Link, to: `/listings/${listing.listing_id}` };

    const Comp = linkProps.as;

    return (
        <Comp
            {...(linkProps.as === Link
                ? { to: linkProps.to }
                : {
                      href: linkProps.href,
                      target: linkProps.target,
                      rel: linkProps.rel,
                  })}
            data-testid={testid || `listing-card-${listing.listing_id}`}
            className="surface overflow-hidden block group hover:border-hellcat"
            style={{ transition: "border-color 0.2s, transform 0.2s" }}
        >
            <div
                className="relative w-full listing-image overflow-hidden"
                style={{
                    aspectRatio: "16 / 10",
                    borderTopLeftRadius: 6,
                    borderTopRightRadius: 6,
                }}
            >
                {hero ? (
                    <img
                        src={hero.startsWith("/stock/") ? hero : absUrl(hero)}
                        alt={`${listing.year} ${listing.model_name} ${listing.trim}`}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.03]"
                        style={{ transition: "transform 0.4s ease" }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-onyx-500 label-caps">
                        No Photo
                    </div>
                )}
                {hasDisclaimer && (
                    <div
                        className="absolute bottom-0 left-0 right-0 bg-hellcat/95 text-white text-[10px] font-bold uppercase tracking-widest text-center py-1.5 backdrop-blur-sm"
                        data-testid="photo-disclaimer"
                    >
                        // Not Actual Vehicle · Contact Seller
                    </div>
                )}
                {/* Trim + mods badges — sharp, angular, no pills */}
                <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                    <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1"
                        style={{
                            background: "#E10600",
                            color: "#fff",
                            borderRadius: 3,
                            boxShadow: "0 4px 12px rgba(225,6,0,0.4)",
                        }}
                    >
                        {listing.trim.split(" ")[0]}
                    </span>
                    {listing.mods_level && listing.mods_level !== "Stock" && (
                        <span
                            className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1"
                            style={{
                                background: "rgba(10,10,10,0.85)",
                                color: "#fff",
                                border: "1px solid rgba(255,255,255,0.15)",
                                borderRadius: 3,
                                backdropFilter: "blur(6px)",
                            }}
                        >
                            {listing.mods_level === "Full Send"
                                ? "Full Send"
                                : listing.mods_level === "Track"
                                ? "Track"
                                : "Street"}
                        </span>
                    )}
                </div>
                {/* Price overlay — angular */}
                <div
                    className="absolute bottom-3 right-3 headline text-xl px-3 py-1.5"
                    style={{
                        background: "rgba(10,10,10,0.9)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 4,
                        backdropFilter: "blur(8px)",
                    }}
                >
                    {formatPrice(listing.price)}
                    {listing.under_market_pct >= 8 && (
                        <div
                            className="text-[10px] font-bold mt-0.5 tracking-widest"
                            style={{ color: "#86efac" }}
                            data-testid={`under-market-${listing.listing_id}`}
                        >
                            ↓ {listing.under_market_pct}% UNDER MKT
                        </div>
                    )}
                </div>
            </div>
            <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div
                            className="text-lg text-white leading-tight truncate"
                            style={{
                                fontFamily: "'DM Serif Display', serif",
                                fontWeight: 400,
                            }}
                        >
                            {listing.year} {listing.model_name}
                        </div>
                        <div className="text-sm text-onyx-500 mt-0.5 line-clamp-1">
                            {listing.trim}
                        </div>
                    </div>
                    {!isExternal && listing.seller?.verified_seller && (
                        <BadgeCheck className="w-5 h-5 text-hellcat shrink-0 mt-0.5" />
                    )}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-onyx-500 flex-wrap">
                    {listing.mileage > 0 && (
                        <span className="flex items-center gap-1">
                            <Gauge className="w-3.5 h-3.5" />
                            {formatMileage(listing.mileage)}
                        </span>
                    )}
                    <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {listing.location}
                    </span>
                    {!isExternal && listing.seller?.seller_rating > 0 && (
                        <span className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 fill-hellcat text-hellcat" />
                            {listing.seller.seller_rating.toFixed(1)}
                        </span>
                    )}
                    <span className="ml-auto label-caps text-[10px]">
                        {timeAgo(listing.created_at)}
                    </span>
                </div>
            </div>
        </Comp>
    );
}
